"use client";

import { useState, useCallback, useEffect } from "react";
import { Search, SlidersHorizontal, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

interface ShopControlsMinimalProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
  isLoading?: boolean;
  onFiltersOpen: () => void;
  activeFiltersCount?: number;
  onClearFilters?: () => void;
}

export function ShopControlsMinimal({
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  isLoading = false,
  onFiltersOpen,
  activeFiltersCount = 0,
  onClearFilters,
}: ShopControlsMinimalProps) {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (localSearchQuery !== searchQuery && localSearchQuery.length >= 1) {
      setIsSearching(true);
      const timer = setTimeout(() => setIsSearching(false), 300);
      return () => clearTimeout(timer);
    } else {
      setIsSearching(false);
    }
  }, [localSearchQuery, searchQuery]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearchQuery(value);
      onSearchChange(value);
    },
    [onSearchChange]
  );

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearchSubmit();
    },
    [onSearchSubmit]
  );

  return (
    <div className="border-b border-gray-200 bg-white sticky top-16 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="py-4">
          {/* Mobile & Desktop Controls */}
          <div className="flex items-center justify-between gap-3">
            {/* Search - Limited width on desktop */}
            <form onSubmit={handleSearchSubmit} className="flex-1 sm:flex-none sm:w-72 lg:w-80">
              <div className="relative">
                {isSearching ? (
                  <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                )}
                <Input
                  placeholder="Search products..."
                  value={localSearchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 border-gray-200 focus:border-gray-400 focus:ring-gray-400 bg-white text-gray-900 placeholder:text-gray-400 h-11"
                />
              </div>
            </form>

            {/* Right side - Filter and Clear buttons */}
            <div className="flex items-center gap-3">

              {/* Filters Button - Black with authority */}
              <Button
                onClick={onFiltersOpen}
                className="flex items-center gap-2 bg-black text-white hover:bg-gray-800 h-11 px-5 font-medium"
                disabled={isLoading}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>Filter</span>
                {activeFiltersCount > 0 && (
                  <Badge 
                    variant="secondary" 
                    className="bg-white text-black rounded-full min-w-[20px] h-5 flex items-center justify-center text-xs px-1.5 font-semibold"
                  >
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>

              {/* Clear Filters Button - Only shown when filters are active */}
              {activeFiltersCount > 0 && onClearFilters && (
                <Button
                  onClick={onClearFilters}
                  className="flex items-center gap-1.5 bg-red-100 text-red-700 hover:bg-red-200 h-11 px-4 font-medium"
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Clear</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
